import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Modal, Form, Input, Select, DatePicker, Tag, Space, message, InputNumber, Divider, List, Avatar, Upload, Button, Popconfirm, Collapse, Spin, Typography, Timeline } from 'antd';
import { UserOutlined, PaperClipOutlined, LinkOutlined, FileSearchOutlined, FilePdfOutlined, CalendarOutlined, EditOutlined, NotificationOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

import { useAuth } from '../store/auth';
import { useFileViewer } from '../store/FileViewerContext';
import LinkifyText from './LinkifyText';
import TaskChecklist from './TaskChecklist';
import TaskBPM from './TaskBPM';
import SmartMentionsInput from './SmartMentionsInput';
import UniversalFileUploadModal from './UniversalFileUploadModal';
import FilePickerModal from './FilePickerModal';

import { TasksService } from '../api/services/tasks.service';
import { CalendarService } from '../api/services/calendar.service';
import api from '../api/client';

const statusOptions = [
  { value: 'new', label: 'Новая' },
  { value: 'in_progress', label: 'В работе' },
  { value: 'review', label: 'На проверке' },
  { value: 'done', label: 'Готово' },
  { value: 'cancelled', label: 'Отменена' },
];

const priorityOptions = [
  { value: 'low', label: 'Низкий' },
  { value: 'medium', label: 'Средний' },
  { value: 'high', label: 'Высокий' },
  { value: 'critical', label: 'Критический' },
];

const taskTypeOptions = [
  { value: 'land_legal', label: 'Земельно-правовые' },
  { value: 'permissive', label: 'Разрешительные' },
  { value: 'expert', label: 'Экспертные' },
  { value: 'design_engineering', label: 'Проектно-инженерные' },
  { value: 'financial_estimate', label: 'Финансово-сметные' },
  { value: 'control_supervision', label: 'Контрольно-надзорные' },
  { value: 'logistics', label: 'Логистические' },
  { value: 'coordination', label: 'Координационные' },
  { value: 'sales_marketing', label: 'Продажно-маркетинговые' },
  { value: 'cadastral_registration', label: 'Кадастрово-регистрационные' },
  { value: 'warranty', label: 'Гарантийные (постстроительные)' },
  { value: 'judicial_claim', label: 'Судебно-претензионные' },
  { value: 'mobilization', label: 'Мобилизационные (ЧС/форс-мажор)' },
  { value: 'acceptance_transfer', label: 'Приемо-передаточные (УК)' },
  { value: 'organizational_regime', label: 'Организационно-режимные' },
  { value: 'household_administrative', label: 'Хозяйственно-административные (АХО)' },
  { value: 'gr_tasks', label: 'GR-задачи (административное лоббирование)' },
  { value: 'commercial_negotiations', label: 'Коммерческие переговоры с партнёрами' },
];

const dependencyTypeOptions = [
  { value: 'finish_to_start', label: 'Завершение → Начало (FS)' },
  { value: 'start_to_start', label: 'Начало → Начало (SS)' },
  { value: 'finish_to_finish', label: 'Завершение → Завершение (FF)' },
  { value: 'start_to_finish', label: 'Начало → Завершение (SF)' },
];

const participantRoleOptions = [
  { value: 'watcher', label: 'Наблюдатель' },
  { value: 'validator', label: 'Валидатор' },
  { value: 'approver', label: 'Утверждающий' },
  { value: 'curator', label: 'Куратор' },
  { value: 'assignee', label: 'Исполнитель' },
];

export default function TaskDetailModal({ 
  open, 
  onClose, 
  selectedTask, 
  setSelectedTask,
  users = [], 
  projects = [], 
  tasks = [], 
  onUpdate, // called when a task status/details changes, to trigger parent fetch
  onOpenCreateSubtask // optional callback to handle subtask creation from parent
}) {
  const { user } = useAuth();
  const { openFileCard } = useFileViewer();
  const [delegateModalOpen, setDelegateModalOpen] = useState(false);
  const [dependenciesModalOpen, setDependenciesModalOpen] = useState(false);
  const [scheduleEventModalOpen, setScheduleEventModalOpen] = useState(false);
  const [addMeetingModalOpen, setAddMeetingModalOpen] = useState(false);
  const [upcomingMeetings, setUpcomingMeetings] = useState([]);
  const [meetingToAdd, setMeetingToAdd] = useState(null);
  const [commentText, setCommentText] = useState('');
  const [selectedExistingFile, setSelectedExistingFile] = useState(null);
  const [filePickerOpen, setFilePickerOpen] = useState(false);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [showAllComments, setShowAllComments] = useState(false);
  const [editTaskAdminModalOpen, setEditTaskAdminModalOpen] = useState(false);
  const [exportTaskPdfLoading, setExportTaskPdfLoading] = useState(false);
  const [decisionsModalOpen, setDecisionsModalOpen] = useState(false);
  const [taskDecisions, setTaskDecisions] = useState([]);
  const [decisionsLoading, setDecisionsLoading] = useState(false);
  const [commentSubmitting, setCommentSubmitting] = useState(false);

  const [scheduleEventForm] = Form.useForm();
  const [delegateForm] = Form.useForm();
  const [dependenciesForm] = Form.useForm();
  const [editTaskAdminForm] = Form.useForm();

  const extractFileId = (url) => {
    const match = url?.match(/([a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12})/i);
    return match ? match[1] : null;
  };

  const avatarUrl = (avatarId) => avatarId ? `/api/v1/files/${avatarId}/content` : null;

  const fetchUpcomingMeetings = async () => {
    try {
      const start = dayjs().format('YYYY-MM-DD');
      const end = dayjs().add(14, 'day').format('YYYY-MM-DD');
      const res = await CalendarService.getEvents({ start, end });
      const meetings = res.data.filter(e => (e.event_type === 'meeting' || e.event_type === 'regular_meeting') && e.meeting_id);
      setUpcomingMeetings(meetings);
    } catch (e) {
      console.error(e);
    }
  };

  const handleAddToMeeting = async () => {
    if (!meetingToAdd || !selectedTask) return;
    try {
      const evt = upcomingMeetings.find(e => e.id === meetingToAdd);
      if (!evt || !evt.meeting_id) {
         message.error('Событие не связано с совещанием');
         return;
      }
      await api.post(`/meetings/${evt.meeting_id}/agenda`, {
        task_id: selectedTask.id,
        sort_order: 0
      });
      message.success('Задача добавлена в повестку совещания');
      setAddMeetingModalOpen(false);
      setMeetingToAdd(null);
    } catch (e) {
      if (e.response?.status === 404) {
        message.error('Это совещание было удалено из базы. Пожалуйста, обновите страницу.');
      } else {
        message.error(e.response?.data?.detail || 'Ошибка добавления в повестку');
      }
    }
  };

  const handleExportTaskPDF = async () => {
    if (!selectedTask) return;
    setExportTaskPdfLoading(true);
    message.loading({ content: 'Генерация PDF...', key: 'pdf-task-gen' });
    await new Promise(r => setTimeout(r, 1000));
    try {
      const doc = new jsPDF({ orientation: 'portrait', unit: 'px', format: 'a4' });
      
      const page1 = document.getElementById('pdf-task-page-1');
      if (page1) {
        const canvas1 = await html2canvas(page1, { scale: 2, useCORS: true });
        const img1 = canvas1.toDataURL('image/png');
        const pdfWidth = doc.internal.pageSize.getWidth();
        const pdfHeight = (canvas1.height * pdfWidth) / canvas1.width;
        doc.addImage(img1, 'PNG', 0, 0, pdfWidth, pdfHeight);
      }

      const page2 = document.getElementById('pdf-task-page-2');
      if (page2) {
        doc.addPage('a4', 'portrait');
        const canvas2 = await html2canvas(page2, { scale: 2, useCORS: true });
        const img2 = canvas2.toDataURL('image/png');
        const pdfWidth = doc.internal.pageSize.getWidth();
        const pdfHeight = (canvas2.height * pdfWidth) / canvas2.width;
        doc.addImage(img2, 'PNG', 0, 0, pdfWidth, pdfHeight);
      }

      doc.save(`task_${selectedTask.id}_export.pdf`);
      message.success({ content: 'PDF успешно сгенерирован!', key: 'pdf-task-gen' });
    } catch (err) {
      console.error('Task PDF export error:', err);
      message.error({ content: 'Ошибка генерации PDF', key: 'pdf-task-gen' });
    }
    setExportTaskPdfLoading(false);
  };

  const handleOpenDecisions = async () => {
    if (!selectedTask) return;
    setDecisionsModalOpen(true);
    setDecisionsLoading(true);
    try {
      const res = await api.get(`/tasks/${selectedTask.id}/decisions`);
      setTaskDecisions(res.data?.decisions || []);
    } catch (err) {
      message.error('Ошибка загрузки решений');
    } finally {
      setDecisionsLoading(false);
    }
  };

  const refreshTask = async () => {
    if (!selectedTask?.id) return;
    try {
      const response = await TasksService.getTaskById(selectedTask.id);
      setSelectedTask(response.data);
      if (onUpdate) onUpdate();
    } catch (e) {
      console.error('Failed to refresh task details', e);
    }
  };

  const handleUpdateTaskAdmin = async (values) => {
    if (!selectedTask) return;
    try {
      await TasksService.updateTask(selectedTask.id, {
        title: values.title,
        description: values.description,
        project_id: values.project_id,
        parent_id: values.parent_id
      });
      message.success('Задача успешно обновлена');
      setEditTaskAdminModalOpen(false);
      refreshTask();
    } catch (e) {
      message.error(e.response?.data?.detail || 'Ошибка при обновлении задачи');
    }
  };

  const handleDelegateOrShiftDeadline = async (values) => {
    if (!selectedTask) return;
    try {
      const { new_assignee_id, new_watcher_id, new_actual_due_date, comment } = values;
      
      const updatePayload = {};
      if (new_assignee_id) updatePayload.assignee_id = new_assignee_id;
      if (new_actual_due_date) updatePayload.actual_due_date = new_actual_due_date.format('YYYY-MM-DD');
      
      if (Object.keys(updatePayload).length > 0) {
        await TasksService.updateTask(selectedTask.id, updatePayload);
      }
      
      if (new_watcher_id) {
        await TasksService.addParticipants(selectedTask.id, { user_id: new_watcher_id, role: 'watcher' });
      }
      
      const commentFormData = new FormData();
      commentFormData.append('content', `[Делегирование/Сроки] ${comment}`);
      await TasksService.addCommentWithFile(selectedTask.id, commentFormData);
      
      message.success('Изменения сохранены');
      setDelegateModalOpen(false);
      delegateForm.resetFields();
      refreshTask();
    } catch (e) {
      const d = e.response?.data?.detail;
      message.error(Array.isArray(d) ? 'Ошибка: проверьте правильность заполнения полей' : (d || 'Ошибка сохранения изменений'));
    }
  };

  const handleScheduleEvent = async (values) => {
    if (!selectedTask) return;
    try {
      const { event_title, event_type, start_time, end_time, event_link, participants } = values;
      
      const payload = {
        title: event_title,
        description: `Событие создано из задачи #${selectedTask.id}`,
        event_type: event_type || 'event',
        start_time: start_time.toISOString(),
        end_time: end_time.toISOString(),
        location: event_link || null,
        participant_ids: participants || [],
        owner_id: user.id,
        task_id: selectedTask.id
      };

      await CalendarService.createEvent(payload);

      const participantNames = participants && participants.length > 0
        ? participants.map(id => users.find(u => String(u.id) === String(id))?.full_name || `ID: ${id}`).join(', ')
        : 'Нет дополнительных участников';
      
      const commentContent = `📅 Запланировано новое событие: **${event_title}**\n🕒 Время: ${start_time.format('DD.MM.YYYY HH:mm')} - ${end_time.format('HH:mm')}\nУчастники: ${participantNames}\n🔗 [Перейти в календарь](/calendar)`;
      
      const formData = new FormData();
      formData.append('content', commentContent);
      
      await TasksService.addCommentWithFile(selectedTask.id, formData);
      message.success('Событие успешно запланировано');
      setScheduleEventModalOpen(false);
      scheduleEventForm.resetFields();
      
      refreshTask();
    } catch (e) {
      message.error(e.response?.data?.detail || 'Ошибка планирования события');
    }
  };

  const handleAddDependency = async (values) => {
    if (!selectedTask) return;
    try {
      await TasksService.addDependencies(selectedTask.id, values);
      message.success('Зависимость добавлена');
      setDependenciesModalOpen(false);
      dependenciesForm.resetFields();
      refreshTask();
    } catch(e) {
      message.error('Ошибка добавления зависимости');
    }
  };

  const handleStatusChange = async (id, status) => {
    try {
      await TasksService.patchTask(id, { status });
      refreshTask();
    } catch (e) {
      const d = e.response?.data?.detail;
      message.error(Array.isArray(d) ? 'Ошибка валидации' : (d || 'Ошибка изменения статуса'));
    }
  };

  const handleProgressTask = async (task) => {
    let nextStatus = '';
    if (task.status === 'new') {
      nextStatus = 'in_progress';
    } else if (task.status === 'in_progress') {
      nextStatus = 'review';
    } else if (task.status === 'review') {
      nextStatus = 'done';
    }
    
    if (nextStatus) {
      await handleStatusChange(task.id, nextStatus);
    }
  };

  const renderProgressAction = (task, size = "middle") => {
    if (!task) return null;
    
    const isAdmin = (user?.role_level >= 5 || user?.username === 'khaminov');
    const isAssignee = user?.id === task.assignee_id;
    
    const isValidator = task.participants?.some(p => 
      Number(p.user_id) === Number(user?.id) && p.role === 'validator'
    );
    // Есть ли у задачи активный валидатор (участник с ролью validator)
    const hasValidator = task.participants?.some(p => p.role === 'validator');
    // Куратор/постановщик/создатель — могут принять задачу, если валидатора нет
    const isController = Number(task.initiator_id) === Number(user?.id) ||
      Number(task.curator_id) === Number(user?.id) ||
      Number(task.creator_id) === Number(user?.id);

    if (task.status === 'new' && (isAssignee || isAdmin)) {
      return <Button key="progress" size={size} type="primary" onClick={() => handleProgressTask(task)}>Взять в работу</Button>;
    }
    if (task.status === 'in_progress' && (isAssignee || isAdmin)) {
      const hasResult = task.comments && task.comments.some(c => c.is_result);
      return (
        <Button key="progress" size={size} type="primary" onClick={() => handleProgressTask(task)} disabled={!hasResult}>
          Отправить на контроль
        </Button>
      );
    }
    if (task.status === 'review' && (isValidator || isAdmin || (!hasValidator && (isController || isAssignee)))) {
      return (
        <Space key="progress">
          <Button size={size} type="primary" onClick={() => handleProgressTask(task)}>Принять (Завершить)</Button>
          <Button size={size} danger onClick={() => handleStatusChange(task.id, 'in_progress')}>Отклонить</Button>
        </Space>
      );
    }
    return null;
  };

  const handleAddComment = async (uploadData = null) => {
    if (!commentText.trim() && !selectedExistingFile && !uploadData) return;
    if (!selectedTask || commentSubmitting) return;
    
    const textToSubmit = commentText;
    const fileToSubmit = selectedExistingFile;
    setCommentSubmitting(true);
    setCommentText('');
    setSelectedExistingFile(null);
    
    try {
      const formData = new FormData();
      formData.append('content', textToSubmit || (uploadData ? 'Прикреплен файл' : ''));
      
      if (uploadData) {
        const files = uploadData.file.getAll('file');
        files.forEach(f => formData.append('file', f));
        formData.append('title', uploadData.title);
        formData.append('file_type', uploadData.fileType);
        if (uploadData.projectId) formData.append('project_id', uploadData.projectId);
        if (uploadData.description) formData.append('description', uploadData.description);
      }
      
      if (fileToSubmit) {
        formData.append('existing_file_url', fileToSubmit.view_url || `/api/v1/files/${fileToSubmit.id}/content`);
        formData.append('existing_file_name', fileToSubmit.original_name);
      }
      
      await TasksService.addCommentWithFile(selectedTask.id, formData);
      message.success('Комментарий добавлен');
      if (uploadData) setUploadModalOpen(false);
      refreshTask();
    } catch (error) {
      setCommentText(textToSubmit);
      setSelectedExistingFile(fileToSubmit);
      message.error('Ошибка добавления комментария');
    } finally {
      setCommentSubmitting(false);
    }
  };

  const handleSubtaskClick = async (subtask) => {
    try {
      const response = await TasksService.getTaskById(subtask.id);
      setSelectedTask(response.data);
    } catch(e) {
      console.error(e);
      setSelectedTask(subtask);
    }
  };

  return (
    <>
      <Modal 
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span>{`Задача: ${selectedTask?.title || 'Загрузка...'}`}</span>
            {selectedTask && (
              <Space>
                <Button
                  type="text"
                  icon={<EditOutlined />}
                  onClick={() => {
                    editTaskAdminForm.setFieldsValue({
                      title: selectedTask.title,
                      description: selectedTask.description,
                      project_id: selectedTask.project_id,
                      parent_id: selectedTask.parent_id
                    });
                    setEditTaskAdminModalOpen(true);
                  }}
                  title="Редактировать задачу"
                />
                <Button 
                  type="text" 
                  icon={<LinkOutlined />} 
                  onClick={() => {
                    navigator.clipboard.writeText(`${window.location.origin}/tasks?taskId=${selectedTask.id}`);
                    message.success('Ссылка на задачу скопирована');
                  }}
                  title="Скопировать ссылку"
                />
                <Button
                  type="text"
                  icon={<CalendarOutlined />}
                  onClick={() => {
                    fetchUpcomingMeetings();
                    setAddMeetingModalOpen(true);
                  }}
                  title="Добавить в повестку предстоящего совещания"
                />
                <Button
                  type="text"
                  icon={<NotificationOutlined />}
                  onClick={handleOpenDecisions}
                  title="Упоминания и решения в совещаниях"
                />
              </Space>
            )}
          </div>
        }
        open={open}
        onCancel={onClose}
        footer={[
          selectedTask && (user?.role_level >= 5 || user?.username === 'khaminov') && (
            <Popconfirm
              key="delete"
              title="Вы уверены, что хотите удалить эту задачу?"
              onConfirm={async () => {
                try {
                  await TasksService.deleteTask(selectedTask.id);
                  message.success('Задача успешно удалена');
                  onClose();
                  if (onUpdate) onUpdate();
                } catch (error) {
                  message.error('Ошибка удаления задачи');
                }
              }}
              okText="Удалить"
              cancelText="Отмена"
            >
              <Button danger>Удалить задачу</Button>
            </Popconfirm>
          ),
          renderProgressAction(selectedTask, "middle"),
          <Button key="export_pdf" icon={<FilePdfOutlined />} onClick={handleExportTaskPDF} loading={exportTaskPdfLoading} disabled={!selectedTask}>
            PDF
          </Button>,
          onOpenCreateSubtask && (
            <Button key="create_subtask" type="dashed" onClick={() => onOpenCreateSubtask(selectedTask)} disabled={!selectedTask}>
              Создать подзадачу
            </Button>
          ),
          <Button key="delegate" onClick={() => setDelegateModalOpen(true)} disabled={!selectedTask || !(user?.role_level >= 5 || user?.username === 'khaminov' || user?.id === selectedTask?.initiator_id || user?.id === selectedTask?.curator_id || user?.id === selectedTask?.assignee_id || user?.id === selectedTask?.creator_id)}>
            Делегирование/Сроки
          </Button>,
          <Button 
            key="schedule_event" 
            onClick={() => {
              if (selectedTask) {
                scheduleEventForm.setFieldsValue({
                  event_title: `Обсуждение: ${selectedTask.title}`,
                  event_type: 'event',
                  start_time: dayjs().add(1, 'hour').startOf('hour'),
                  end_time: dayjs().add(2, 'hour').startOf('hour'),
                  participants: [selectedTask.assignee_id, selectedTask.curator_id].filter(Boolean)
                });
                setScheduleEventModalOpen(true);
              }
            }} 
            disabled={!selectedTask}
          >
            Назначить событие
          </Button>,
          <Button key="dependencies" onClick={() => setDependenciesModalOpen(true)} disabled={!selectedTask}>
            Зависимости
          </Button>,
          <Button key="close" onClick={onClose}>
            Закрыть
          </Button>,
        ].filter(Boolean)}
        width={1000}
      >
        {!selectedTask ? (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <p>Загрузка данных задачи...</p>
          </div>
        ) : (
          <div className="task-detail-container">
            <Collapse
              defaultActiveKey={['1', '7']}
              expandIconPosition="end"
              style={{ marginBottom: 24 }}
              items={[
                {
                  key: '1',
                  label: <b>Задача, Описание, Проект</b>,
                  children: (
                    <div>
                      <p><strong>Описание:</strong> {selectedTask.description ? <LinkifyText text={selectedTask.description} style={{ display: 'inline' }} /> : 'Нет описания'}</p>
                      {selectedTask.parent_id && (() => {
                        const parentTask = tasks.find(t => t.id === selectedTask.parent_id);
                        return (
                          <p><strong>Родительская задача:</strong> {parentTask ? (
                            <Link to={`/tasks?taskId=${parentTask.id}`} style={{ color: '#1890ff' }}>
                              {parentTask.title}
                            </Link>
                          ) : (
                            <Link to={`/tasks?taskId=${selectedTask.parent_id}`} style={{ color: '#1890ff' }}>
                              ID: {selectedTask.parent_id}
                            </Link>
                          )}</p>
                        );
                      })()}
                      <p><strong>Тип:</strong> {taskTypeOptions.find(opt => opt.value === selectedTask.task_type)?.label || selectedTask.task_type}</p>
                      <p><strong>Проект:</strong> {projects.find(p => String(p.id) === String(selectedTask.project_id))?.name || `ID: ${selectedTask.project_id}`}</p>
                      <p><strong>Постановщик:</strong> {selectedTask.initiator_name || users.find(u => String(u.id) === String(selectedTask.initiator_id))?.full_name || `ID: ${selectedTask.initiator_id}`}</p>
                    </div>
                  )
                },
                {
                  key: '2',
                  label: <b>Основная информация (Статус и даты)</b>,
                  children: (
                    <div>
                      <p><strong>Статус:</strong> {statusOptions.find(opt => opt.value === selectedTask.status)?.label || selectedTask.status}</p>
                      <p><strong>Приоритет:</strong> {priorityOptions.find(opt => opt.value === selectedTask.priority)?.label || selectedTask.priority}</p>
                      <p><strong>Прогресс:</strong> {selectedTask.progress}%</p>
                      <p><strong>Дата начала:</strong> {selectedTask.start_date ? dayjs(selectedTask.start_date).format('DD.MM.YYYY') : 'Не указана'}</p>
                      <p><strong>Срок выполнения (назначенный):</strong> {selectedTask.due_date ? dayjs(selectedTask.due_date).format('DD.MM.YYYY') : 'Не указан'}</p>
                      <p><strong>Срок выполнения (актуализированный):</strong> {selectedTask.actual_due_date ? dayjs(selectedTask.actual_due_date).format('DD.MM.YYYY') : 'Не указан'}</p>
                      <p><strong>Дата краха:</strong> {selectedTask.crash_date ? dayjs(selectedTask.crash_date).format('DD.MM.YYYY') : 'Не указана'}</p>
                    </div>
                  )
                },
                {
                  key: '3',
                  label: <b>Участники задачи</b>,
                  children: (
                    <div>
                      <p><strong>Постановщик:</strong> {selectedTask.initiator_name || users.find(u => String(u.id) === String(selectedTask.initiator_id))?.full_name || `ID: ${selectedTask.initiator_id}`}</p>
                      <p><strong>Куратор:</strong> {selectedTask.curator_id ? (selectedTask.curator_name || users.find(u => String(u.id) === String(selectedTask.curator_id))?.full_name || `ID: ${selectedTask.curator_id}`) : 'Не назначен'}</p>
                      <p><strong>Исполнитель:</strong> {selectedTask.assignee_id ? (selectedTask.assignee_name || users.find(u => String(u.id) === String(selectedTask.assignee_id))?.full_name || `ID: ${selectedTask.assignee_id}`) : 'Не назначен'}</p>
                      {selectedTask.participants?.find(p => p.role === 'validator') && (
                        <>
                          <p><strong>Валидатор:</strong> {
                            (() => {
                              const validator = selectedTask.participants.find(p => p.role === 'validator');
                              const userObj = users.find(u => String(u.id) === String(validator.user_id));
                              return validator.full_name || (userObj ? userObj.full_name || userObj.email : null) || `ID: ${validator.user_id}`;
                            })()
                          }</p>
                        </>
                      )}
                      <Divider style={{ margin: '12px 0' }} />
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        {selectedTask.participants?.filter(p => p.role !== 'validator').map(p => {
                          const userObj = users.find(u => String(u.id) === String(p.user_id));
                          const roleLabel = participantRoleOptions.find(opt => opt.value === p.role)?.label || p.role;
                          return (
                            <div key={p.id}>
                              <Tag color="blue">{roleLabel}</Tag>
                              {p.full_name || (userObj ? userObj.full_name || userObj.email : null) || `ID: ${p.user_id}`}
                            </div>
                          );
                        })}
                        {(!selectedTask.participants || selectedTask.participants.filter(p => p.role !== 'validator').length === 0) && (
                          <span style={{ color: 'gray' }}>Нет дополнительных участников</span>
                        )}
                      </div>
                    </div>
                  )
                },
                {
                  key: '4',
                  label: <b>Метрики и системная информация</b>,
                  children: (
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
                      <div>
                        <p><strong>Оценка часов:</strong> {selectedTask.estimated_hours || 'Не указано'}</p>
                        <p><strong>Бюджет:</strong> {selectedTask.budget ? `${selectedTask.budget} ₽` : 'Не указан'}</p>
                        <p><strong>Блокировка:</strong> {selectedTask.is_blocked ? 'Да (зависимости не выполнены)' : 'Нет'}</p>
                        <p><strong>Требует валидации:</strong> {selectedTask.requires_validation ? 'Да' : 'Нет'}</p>
                      </div>
                      <div>
                        <p><strong>Создана:</strong> {dayjs(selectedTask.created_at).format('DD.MM.YYYY HH:mm')}</p>
                        <p><strong>Обновлена:</strong> {dayjs(selectedTask.updated_at).format('DD.MM.YYYY HH:mm')}</p>
                        <p><strong>Завершена:</strong> {selectedTask.completed_at ? dayjs(selectedTask.completed_at).format('DD.MM.YYYY HH:mm') : 'Нет'}</p>
                        <p><strong>КСГ работа:</strong> {selectedTask.ksg_work_id ? `ID: ${selectedTask.ksg_work_id}` : 'Не связана'}</p>
                      </div>
                    </div>
                  )
                },
                ...(selectedTask.subtasks && selectedTask.subtasks.length > 0 ? [{
                  key: '5',
                  label: <b>Подзадачи</b>,
                  children: (
                    <List
                      size="small"
                      dataSource={selectedTask.subtasks}
                      renderItem={(subtask) => (
                        <List.Item>
                          <Button 
                            type="link" 
                            onClick={() => handleSubtaskClick(subtask)}
                            style={{ padding: 0 }}
                          >
                            {subtask.title}
                          </Button>
                          <span style={{ marginLeft: 16, color: 'gray' }}>
                            Статус: {statusOptions.find(opt => opt.value === subtask.status)?.label || subtask.status}
                          </span>
                        </List.Item>
                      )}
                    />
                  )
                }] : []),
                {
                  key: '6',
                  label: <b>Чек-лист задачи ({selectedTask.checklists?.length || 0})</b>,
                  children: (
                    <TaskChecklist 
                      task={selectedTask} 
                      users={users} 
                      onUpdate={refreshTask} 
                    />
                  )
                },
                {
                  key: '8',
                  label: <b>Маршруты согласования</b>,
                  children: (
                    <TaskBPM task={selectedTask} />
                  )
                },
                {
                  key: '9',
                  label: <b>История активности</b>,
                  children: (
                    <div style={{ maxHeight: '400px', overflowY: 'auto', paddingRight: '10px' }}>
                      <List
                        size="small"
                        dataSource={[...(selectedTask.history || [])].sort((a, b) => new Date(b.created_at) - new Date(a.created_at))}
                        locale={{ emptyText: 'Нет истории изменений' }}
                        renderItem={(item) => {
                          const author = users.find(u => String(u.id) === String(item.user_id));
                          const authorName = item.author_full_name || (author ? author.full_name || author.email : null) || `ID: ${item.user_id}`;
                          
                          let actionText = '';
                          if (item.action === 'comment_added') {
                            actionText = 'добавил комментарий';
                          } else if (item.action === 'participant_added') {
                            actionText = item.new_value || 'добавил участника';
                          } else if (item.action === 'participant_removed') {
                            actionText = item.old_value || 'удалил участника';
                          } else if (item.old_value && item.new_value) {
                            actionText = `изменил поле: ${item.old_value} ➔ ${item.new_value}`;
                          } else if (item.new_value) {
                            actionText = `установил значение: ${item.new_value}`;
                          } else {
                            actionText = `изменил параметр: ${item.field_name || item.action}`;
                          }

                          return (
                            <List.Item>
                              <List.Item.Meta
                                description={
                                  <div style={{ fontSize: '13px' }}>
                                    <span style={{ color: 'rgba(0, 0, 0, 0.85)', fontWeight: 500 }}>{authorName}</span>
                                    <span style={{ margin: '0 8px', color: 'gray' }}>{dayjs(item.created_at).format('DD.MM.YYYY HH:mm:ss')}</span>
                                    <br />
                                    <span>{actionText}</span>
                                  </div>
                                }
                              />
                            </List.Item>
                          );
                        }}
                      />
                    </div>
                  )
                },
                {
                  key: '7',
                  label: <b>Комментарии ({selectedTask.comments?.length || 0})</b>,
                  children: (
                    <div>
                      <List
                        itemLayout="horizontal"
                        dataSource={showAllComments ? selectedTask.comments || [] : (selectedTask.comments || []).slice(-20)}
                        locale={{ emptyText: 'Нет комментариев' }}
                        renderItem={(comment) => {
                          const author = users.find(u => String(u.id) === String(comment.user_id));
                          return (
                            <List.Item>
                              <List.Item.Meta
                                avatar={
                                  <Avatar 
                                    src={author?.avatar_file_id ? avatarUrl(author.avatar_file_id) : null} 
                                    icon={!author?.avatar_file_id && <UserOutlined />} 
                                  />
                                }
                                title={comment.author_full_name || (author ? author.full_name || author.email : null) || `ID: ${comment.user_id}`}
                                description={
                                  <>
                                    <LinkifyText text={comment.content} />
                                    {comment.attached_files && comment.attached_files.length > 0 ? (
                                      <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 4 }}>
                                        {comment.attached_files.map((f, i) => (
                                          <a key={i} onClick={() => {
                                            const id = f.file_id || extractFileId(f.file_url);
                                            if (id) {
                                              openFileCard(id, { task_id: selectedTask.id, project_id: selectedTask.project_id });
                                            } else {
                                              window.open(f.file_url, '_blank');
                                            }
                                          }} style={{ 
                                            cursor: 'pointer', 
                                            color: '#1890ff',
                                            border: comment.file_approval_status === 'active' ? '2px solid #faad14' : comment.file_approval_status === 'cancelled' ? '2px solid #f5222d' : comment.file_approval_status === 'completed' ? '2px solid #52c41a' : 'none',
                                            backgroundColor: comment.file_approval_status === 'active' ? 'rgba(250, 173, 20, 0.05)' : comment.file_approval_status === 'completed' ? 'rgba(82, 196, 26, 0.05)' : comment.file_approval_status === 'cancelled' ? 'rgba(245, 34, 45, 0.05)' : 'transparent',
                                            padding: comment.file_approval_status ? '2px 4px' : '0',
                                            borderRadius: comment.file_approval_status ? '4px' : '0',
                                            display: 'inline-block'
                                          }}>
                                            <PaperClipOutlined /> {f.file_name || 'Прикрепленный файл'}
                                          </a>
                                        ))}
                                      </div>
                                    ) : comment.file_url ? (
                                      <div style={{ marginTop: 8 }}>
                                        <a onClick={() => {
                                          const id = extractFileId(comment.file_url);
                                          if (id) {
                                            openFileCard(id, { task_id: selectedTask.id, project_id: selectedTask.project_id });
                                          } else {
                                            window.open(comment.file_url, '_blank');
                                          }
                                        }} style={{ 
                                          cursor: 'pointer', 
                                          color: '#1890ff',
                                          border: comment.file_approval_status === 'active' ? '2px solid #faad14' : comment.file_approval_status === 'cancelled' ? '2px solid #f5222d' : comment.file_approval_status === 'completed' ? '2px solid #52c41a' : 'none',
                                          backgroundColor: comment.file_approval_status === 'active' ? 'rgba(250, 173, 20, 0.05)' : comment.file_approval_status === 'completed' ? 'rgba(82, 196, 26, 0.05)' : comment.file_approval_status === 'cancelled' ? 'rgba(245, 34, 45, 0.05)' : 'transparent',
                                          padding: comment.file_approval_status ? '2px 4px' : '0',
                                          borderRadius: comment.file_approval_status ? '4px' : '0',
                                          display: 'inline-block'
                                        }}>
                                          <PaperClipOutlined /> {comment.file_name || 'Прикрепленный файл'}
                                        </a>
                                      </div>
                                    ) : null}
                                    <div style={{ marginTop: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                      <span style={{ fontSize: '0.8em', color: 'gray' }}>
                                        {dayjs(comment.created_at).format('DD.MM.YYYY HH:mm')}
                                      </span>
                                      
                                      {user?.id === selectedTask.assignee_id && (
                                        <Button 
                                          type={comment.is_result ? "primary" : "default"} 
                                          size="small" 
                                          onClick={async () => {
                                            try {
                                              await api.patch(`/tasks/comments/${comment.id}/result`);
                                              refreshTask();
                                              message.success(comment.is_result ? 'Метка результата снята' : 'Отмечено как результат');
                                            } catch (e) {
                                              message.error('Ошибка изменения статуса результата');
                                            }
                                          }}
                                        >
                                          {comment.is_result ? 'Снять метку результата' : 'Отметить как результат'}
                                        </Button>
                                      )}
                                    </div>
                                  </>
                                }
                                style={{
                                  border: comment.is_result ? '2px solid #52c41a' : 'none',
                                  padding: comment.is_result ? '8px' : '0',
                                  borderRadius: comment.is_result ? '8px' : '0',
                                  backgroundColor: comment.is_result ? '#f6ffed' : 'transparent'
                                }}
                              />
                            </List.Item>
                          );
                        }}
                      />
                      {(selectedTask.comments || []).length > 20 && !showAllComments && (
                        <div style={{ textAlign: 'center', marginTop: 10 }}>
                          <span style={{ color: 'gray', marginRight: 10 }}>Показаны последние 20 комментариев из {selectedTask.comments.length}</span>
                          <Button type="link" onClick={() => setShowAllComments(true)}>Показать все комментарии ({selectedTask.comments.length})</Button>
                        </div>
                      )}
                      {(selectedTask.comments || []).length > 20 && showAllComments && (
                        <div style={{ textAlign: 'center', marginTop: 10 }}>
                          <Button type="link" onClick={() => setShowAllComments(false)}>Скрыть комментарии</Button>
                        </div>
                      )}
                      
                      <div style={{ marginTop: 16 }}>
                        <SmartMentionsInput 
                          rows={3} 
                          value={commentText} 
                          onChange={(val) => setCommentText(val)} 
                          placeholder="Добавить комментарий..."
                          onSubmit={() => handleAddComment(null)}
                          disabled={commentSubmitting}
                        />
                        
                        {selectedExistingFile && (
                          <div style={{ marginTop: 8 }}>
                            <Tag closable onClose={() => setSelectedExistingFile(null)} color="blue">
                              <FileSearchOutlined /> Выбранный файл: {selectedExistingFile.original_name}
                            </Tag>
                          </div>
                        )}
                        
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 8 }}>
                          <Space>
                            <Button icon={<PaperClipOutlined />} disabled={!!selectedExistingFile || commentSubmitting} onClick={() => setUploadModalOpen(true)}>Загрузить файл</Button>
                            <Button 
                              icon={<FileSearchOutlined />} 
                              disabled={commentSubmitting}
                              onClick={() => setFilePickerOpen(true)}
                            >
                              Выбрать из файлов
                            </Button>
                <Button
                  type="text"
                  icon={<NotificationOutlined />}
                  onClick={handleOpenDecisions}
                  title="Все упоминания и решения в совещаниях"
                />
              </Space>
                          <Button type="primary" onClick={() => handleAddComment(null)} loading={commentSubmitting} disabled={commentSubmitting}>
                            Отправить
                          </Button>
                        </div>
                      </div>
                    </div>
                  )
                }
              ]}
            />
          </div>
        )}
      </Modal>

      <Modal
        title="Добавить в повестку совещания"
        open={addMeetingModalOpen}
        onCancel={() => {
          setAddMeetingModalOpen(false);
          setMeetingToAdd(null);
        }}
        onOk={handleAddToMeeting}
        okText="Добавить"
        cancelText="Отмена"
      >
        <div style={{ marginBottom: 16 }}>
          Выберите предстоящее совещание (в ближайшие 14 дней):
        </div>
        <Select
          style={{ width: '100%' }}
          placeholder="Выберите совещание"
          value={meetingToAdd}
          onChange={setMeetingToAdd}
          options={upcomingMeetings.map(m => ({
            value: m.id,
            label: `${m.title} (${dayjs(m.start_time).format('DD.MM.YYYY HH:mm')})`
          }))}
        />
      </Modal>

      <UniversalFileUploadModal
        open={uploadModalOpen}
        onCancel={() => setUploadModalOpen(false)}
        onUpload={(data) => handleAddComment(data)}
        defaultTaskId={selectedTask?.id}
        defaultProjectId={selectedTask?.project_id}
      />

      <FilePickerModal 
        open={filePickerOpen} 
        onCancel={() => setFilePickerOpen(false)} 
        onSelect={(file) => {
          setSelectedExistingFile(file);
          setFilePickerOpen(false);
        }} 
      />

      <Modal
        title="Делегирование / Сроки"
        open={delegateModalOpen}
        onCancel={() => setDelegateModalOpen(false)}
        onOk={() => delegateForm.submit()}
        okText="Применить"
        cancelText="Отмена"
      >
        <Form form={delegateForm} layout="vertical" onFinish={handleDelegateOrShiftDeadline}>
          <Form.Item name="new_assignee_id" label="Сменить исполнителя">
            <Select 
              allowClear
              showSearch optionFilterProp="label" filterOption={(input, option) => String(option?.label ?? "").toLowerCase().includes(input.toLowerCase())} options={users.map(u => ({ value: u.id, label: u.full_name || u.email }))}
              placeholder="Выберите нового исполнителя"
            />
          </Form.Item>
          
          <Form.Item name="new_watcher_id" label="Добавить наблюдателя">
            <Select 
              allowClear
              showSearch optionFilterProp="label" filterOption={(input, option) => String(option?.label ?? "").toLowerCase().includes(input.toLowerCase())} options={users.map(u => ({ value: u.id, label: u.full_name || u.email }))}
              placeholder="Выберите пользователя"
            />
          </Form.Item>
          
          <Form.Item name="new_actual_due_date" label="Новый срок выполнения">
            <DatePicker style={{ width: '100%' }} format="DD.MM.YYYY" placeholder="Выберите дату" />
          </Form.Item>

          <Form.Item name="comment" label="Обоснование / Комментарий" rules={[{ required: true, message: 'Пожалуйста, укажите причину изменений' }]}>
            <SmartMentionsInput rows={4} placeholder="Опишите причину делегирования или изменения сроков..." />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Назначить событие"
        open={scheduleEventModalOpen}
        onCancel={() => setScheduleEventModalOpen(false)}
        onOk={() => scheduleEventForm.submit()}
        okText="Запланировать"
        cancelText="Отмена"
      >
        <Form form={scheduleEventForm} layout="vertical" onFinish={handleScheduleEvent}>
          <Form.Item name="event_title" label="Название события" rules={[{ required: true, message: 'Укажите название' }]}>
            <Input placeholder="Например: Обсуждение задачи" />
          </Form.Item>
          <Form.Item name="event_type" label="Тип события" rules={[{ required: true }]}>
            <Select>
              <Select.Option value="event">Событие</Select.Option>
              <Select.Option value="meeting">Совещание</Select.Option>
              <Select.Option value="videoconference">Видеоконференция</Select.Option>
            </Select>
          </Form.Item>
          <Form.Item name="start_time" label="Начало" rules={[{ required: true, message: 'Выберите время' }]}>
            <DatePicker showTime style={{ width: '100%' }} format="DD.MM.YYYY HH:mm" />
          </Form.Item>
          <Form.Item name="end_time" label="Окончание" rules={[{ required: true, message: 'Выберите время' }]}>
            <DatePicker showTime style={{ width: '100%' }} format="DD.MM.YYYY HH:mm" />
          </Form.Item>
          <Form.Item name="event_link" label="Ссылка (опционально)">
            <Input placeholder="https://..." />
          </Form.Item>
          <Form.Item name="participants" label="Участники">
            <Select 
              mode="multiple"
              allowClear
              showSearch optionFilterProp="label" filterOption={(input, option) => String(option?.label ?? "").toLowerCase().includes(input.toLowerCase())} options={users.map(u => ({ value: u.id, label: u.full_name || u.email }))}
              placeholder="Выберите участников"
            />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Зависимости задачи"
        open={dependenciesModalOpen}
        onCancel={() => setDependenciesModalOpen(false)}
        onOk={() => dependenciesForm.submit()}
        okText="Добавить"
      >
        <Form form={dependenciesForm} layout="vertical" onFinish={handleAddDependency}>
          <Form.Item name="depends_on_id" label="Зависит от задачи" rules={[{ required: true }]}>
            <Select 
              options={tasks.filter(t => t.id !== selectedTask?.id).map(t => ({ 
                value: t.id, 
                label: `${t.id}: ${t.title}` 
              }))}
              placeholder="Выберите задачу"
            />
          </Form.Item>
          
          <Form.Item name="dependency_type" label="Тип зависимости" rules={[{ required: true }]} initialValue="finish_to_start">
            <Select options={dependencyTypeOptions} />
          </Form.Item>
          
          <Form.Item name="lag_days" label="Задержка (дни)" initialValue={0}>
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
        </Form>
        
        {selectedTask && (
          <div style={{ marginTop: '24px' }}>
            <h4>Текущие зависимости:</h4>
            <ul>
              {selectedTask.dependency_tasks?.map(d => {
                const depTask = tasks.find(t => t.id === d.depends_on_id);
                return (
                <li key={d.id}>
                  Зависит от задачи #{d.depends_on_id} {depTask ? `«${depTask.title}»` : ''}: 
                  {dependencyTypeOptions.find(opt => opt.value === d.dependency_type)?.label || d.dependency_type}
                  {d.lag_days > 0 && ` (задержка ${d.lag_days} дней)`}
                </li>
                );
              }) || <li>Нет зависимостей</li>}
            </ul>
          </div>
        )}
      </Modal>

      <Modal
        title="Редактировать задачу"
        open={editTaskAdminModalOpen}
        onCancel={() => setEditTaskAdminModalOpen(false)}
        onOk={() => editTaskAdminForm.submit()}
        okText="Сохранить"
        cancelText="Отмена"
        width={700}
      >
        <Form form={editTaskAdminForm} layout="vertical" onFinish={handleUpdateTaskAdmin}>
          <Form.Item name="title" label="Название" rules={[{ required: true, message: 'Обязательное поле' }]}>
            <Input />
          </Form.Item>
          <Form.Item name="description" label="Описание">
            <SmartMentionsInput rows={5} />
          </Form.Item>
          <Form.Item name="project_id" label="Проект">
            <Select 
              allowClear
              showSearch 
              optionFilterProp="label" 
              options={projects.map(p => ({ value: p.id, label: p.name }))}
              placeholder="Выберите проект"
            />
          </Form.Item>
          <Form.Item name="parent_id" label="Родительская задача">
            <Select 
              allowClear
              showSearch 
              optionFilterProp="label" 
              options={tasks.filter(t => t.id !== selectedTask?.id).map(t => ({ value: t.id, label: t.title }))}
              placeholder="Выберите родительскую задачу"
            />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Упоминания и решения в совещаниях"
        open={decisionsModalOpen}
        onCancel={() => setDecisionsModalOpen(false)}
        footer={null}
        width={700}
      >
        {decisionsLoading ? (
          <div style={{ textAlign: 'center', padding: '20px' }}><Spin /></div>
        ) : taskDecisions.length > 0 ? (
          <div style={{ maxHeight: '600px', overflowY: 'auto', paddingRight: '10px' }}>
            <Timeline
              items={taskDecisions.map(dec => {
                const isRegular = dec.source_type === 'regular_meeting';
                const color = isRegular ? 'blue' : (dec.source_type === 'meeting' ? 'green' : 'orange');
                return {
                  color: color,
                  children: (
                    <>
                      <Space style={{ marginBottom: 8 }}>
                        <Tag color={color}>{dec.source_title}</Tag>
                        <Typography.Text strong>
                          {dec.decision_date ? new Date(dec.decision_date).toLocaleString() : 'Дата неизвестна'}
                        </Typography.Text>
                      </Space>
                      <div style={{ whiteSpace: 'pre-wrap' }}>
                        {dec.decision_text}
                      </div>
                    </>
                  )
                };
              })}
            />
          </div>
        ) : (
          <div style={{ textAlign: 'center', padding: '20px', color: 'gray' }}>
            По данной задаче еще не зафиксировано решений в совещаниях или календаре.
          </div>
        )}
      </Modal>

      {exportTaskPdfLoading && (
        <div style={{ position: 'absolute', top: '-10000px', left: '-10000px', pointerEvents: 'none' }}>
          {selectedTask && (
            <>
              <div id="pdf-task-page-1" style={{ width: '800px', background: '#fff', padding: '40px', fontFamily: 'sans-serif' }}>
                <h2>Задача: {selectedTask.title}</h2>
                <div style={{ display: 'flex', gap: '10px', marginBottom: '20px' }}>
                  <span style={{ padding: '4px 8px', background: '#f0f0f0', borderRadius: '4px' }}>
                    Статус: {statusOptions.find(opt => opt.value === selectedTask.status)?.label || selectedTask.status}
                  </span>
                  <span style={{ padding: '4px 8px', background: '#e6f7ff', color: '#1890ff', borderRadius: '4px' }}>
                    Приоритет: {priorityOptions.find(opt => opt.value === selectedTask.priority)?.label || selectedTask.priority}
                  </span>
                </div>
                
                <div style={{ display: 'flex', gap: '40px', marginBottom: '20px' }}>
                  <div style={{ flex: 1 }}>
                    <h4>Основная информация</h4>
                    <p><strong>Описание:</strong> {selectedTask.description ? <LinkifyText text={selectedTask.description} style={{ display: 'inline' }} /> : 'Нет описания'}</p>
                    <p><strong>Тип:</strong> {taskTypeOptions.find(opt => opt.value === selectedTask.task_type)?.label || selectedTask.task_type}</p>
                    <p><strong>Проект:</strong> {projects.find(p => String(p.id) === String(selectedTask.project_id))?.name || `ID: ${selectedTask.project_id}`}</p>
                    <p><strong>Постановщик:</strong> {selectedTask.initiator_name || users.find(u => String(u.id) === String(selectedTask.initiator_id))?.full_name || `ID: ${selectedTask.initiator_id}`}</p>
                    <p><strong>Куратор:</strong> {selectedTask.curator_id ? (selectedTask.curator_name || users.find(u => String(u.id) === String(selectedTask.curator_id))?.full_name || `ID: ${selectedTask.curator_id}`) : 'Не назначен'}</p>
                    <p><strong>Исполнитель:</strong> {selectedTask.assignee_id ? (selectedTask.assignee_name || users.find(u => String(u.id) === String(selectedTask.assignee_id))?.full_name || `ID: ${selectedTask.assignee_id}`) : 'Не назначен'}</p>
                  </div>
                  <div style={{ flex: 1 }}>
                    <h4>Сроки и метрики</h4>
                    <p><strong>Прогресс:</strong> {selectedTask.progress}%</p>
                    <p><strong>Дата начала:</strong> {selectedTask.start_date ? dayjs(selectedTask.start_date).format('DD.MM.YYYY') : 'Не указана'}</p>
                    <p><strong>Срок выполнения (назначенный):</strong> {selectedTask.due_date ? dayjs(selectedTask.due_date).format('DD.MM.YYYY') : 'Не указан'}</p>
                    <p><strong>Срок выполнения (актуализированный):</strong> {selectedTask.actual_due_date ? dayjs(selectedTask.actual_due_date).format('DD.MM.YYYY') : 'Не указан'}</p>
                    <p><strong>Дата краха:</strong> {selectedTask.crash_date ? dayjs(selectedTask.crash_date).format('DD.MM.YYYY') : 'Не указана'}</p>
                    <p><strong>Оценка часов:</strong> {selectedTask.estimated_hours || 'Не указано'}</p>
                    <p><strong>Бюджет:</strong> {selectedTask.budget ? `${selectedTask.budget} ₽` : 'Не указан'}</p>
                  </div>
                </div>

                <h4>Участники задачи</h4>
                <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '20px' }}>
                  <thead>
                    <tr style={{ background: '#f0f0f0' }}>
                      <th style={{ border: '1px solid #ccc', padding: '8px', textAlign: 'left' }}>Имя</th>
                      <th style={{ border: '1px solid #ccc', padding: '8px', textAlign: 'left' }}>Роль</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedTask.participants?.map(p => {
                      const userObj = users.find(u => String(u.id) === String(p.user_id));
                      return (
                        <tr key={p.id}>
                          <td style={{ border: '1px solid #ccc', padding: '8px' }}>{p.full_name || (userObj ? userObj.full_name || userObj.email : null) || `ID: ${p.user_id}`}</td>
                          <td style={{ border: '1px solid #ccc', padding: '8px' }}>{participantRoleOptions.find(opt => opt.value === p.role)?.label || p.role}</td>
                        </tr>
                      );
                    })}
                    {(!selectedTask.participants || selectedTask.participants.length === 0) && (
                      <tr><td colSpan="2" style={{ border: '1px solid #ccc', padding: '8px', textAlign: 'center' }}>Нет участников</td></tr>
                    )}
                  </tbody>
                </table>

                <h4>Зависимости</h4>
                <ul style={{ marginBottom: '20px' }}>
                  {selectedTask.dependency_tasks?.map(d => {
                    const depTask = tasks.find(t => t.id === d.depends_on_id);
                    return (
                    <li key={d.id}>
                      Зависит от задачи #{d.depends_on_id} {depTask ? `«${depTask.title}»` : ''}: {dependencyTypeOptions.find(opt => opt.value === d.dependency_type)?.label || d.dependency_type} {d.lag_days > 0 && ` (задержка ${d.lag_days} дней)`}
                    </li>
                    );
                  })}
                  {(!selectedTask.dependency_tasks || selectedTask.dependency_tasks.length === 0) && (
                    <li>Нет зависимостей</li>
                  )}
                </ul>
              </div>

              <div id="pdf-task-page-2" style={{ width: '800px', background: '#fff', padding: '40px', fontFamily: 'sans-serif' }}>
                {selectedTask.subtasks && selectedTask.subtasks.length > 0 && (
                  <div style={{ marginBottom: '30px' }}>
                    <h2>Подзадачи</h2>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr style={{ background: '#f0f0f0' }}>
                          <th style={{ border: '1px solid #ccc', padding: '8px', textAlign: 'left' }}>Название</th>
                          <th style={{ border: '1px solid #ccc', padding: '8px', textAlign: 'left' }}>Статус</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedTask.subtasks.map(subtask => (
                          <tr key={subtask.id}>
                            <td style={{ border: '1px solid #ccc', padding: '8px' }}>{subtask.title}</td>
                            <td style={{ border: '1px solid #ccc', padding: '8px' }}>{statusOptions.find(opt => opt.value === subtask.status)?.label || subtask.status}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                <h2>Комментарии</h2>
                {(!selectedTask.comments || selectedTask.comments.length === 0) ? (
                  <p>Нет комментариев</p>
                ) : (
                  <div style={{ marginTop: '20px' }}>
                    {selectedTask.comments.map(comment => {
                      const author = users.find(u => String(u.id) === String(comment.user_id));
                      return (
                        <div key={comment.id} style={{ marginBottom: '16px', borderBottom: '1px solid #eee', paddingBottom: '16px' }}>
                          <strong>{author ? author.full_name || author.email : `ID: ${comment.user_id}`}</strong>
                          <span style={{ color: '#888', marginLeft: '10px', fontSize: '0.9em' }}>{dayjs(comment.created_at).format('DD.MM.YYYY HH:mm')}</span>
                          <LinkifyText style={{ marginTop: '8px' }} text={comment.content} />
                          {comment.file_name && <div style={{ marginTop: '4px', fontSize: '0.9em', color: '#1890ff' }}>📎 {comment.file_name}</div>}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      )}
    </>
  );
}
